import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import moment from 'moment';
import Select from 'react-select';
import { Button, Spinner } from 'reactstrap';
import { RiDownload2Line } from 'react-icons/ri';
import { FaChevronDown } from 'react-icons/fa6';
import CustomTabs from 'components/CustomTabs';
import { fetchTraders } from '../Orders/Addorders/orderService';
import {
  CITY_BY_STATE,
  STATES_BY_COUNTRY,
} from '../MarketListing/MarketListingGraphQL';
import {
  buildLocationReportQuery,
  buildUserReportQuery,
} from './AnalyticsGraphQL';

// ─── Constants ────────────────────────────────────────────────────────────────

const months = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 },
];

const years = [2024, 2025, 2026];

const INDIA_COUNTRY_ID = '101';
const ALL_STATES_OPTION = { value: 'ALL', label: 'All States' };
const ALL_CITIES_OPTION = { value: 'ALL', label: 'All Cities' };

const REPORT_PAGE_SIZE = 10;
const QTY_UNIT = 'MT';
const RATE_UNIT = '/KG';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '-';
  return numeric.toLocaleString();
};

const formatCountWithQty = (count, qty, unit = QTY_UNIT) => {
  const countText = formatNumber(count);
  if (qty === null || qty === undefined) return `${countText} / -`;
  const qtyText = unit ? `${formatNumber(qty)} ${unit}` : formatNumber(qty);
  return `${countText} / ${qtyText}`;
};

const formatQtyWithUnit = (value, unit) => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '-';
  return unit
    ? `${numeric.toLocaleString()} ${unit}`
    : numeric.toLocaleString();
};

const formatRatePerKg = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return `${numeric.toLocaleString()}\u00A0${RATE_UNIT}`;
};

const formatPhoneWithoutCode = (_callingCode, phone) => {
  const normalizedPhone =
    phone === null || phone === undefined ? '' : String(phone).trim();
  if (!normalizedPhone) return '';
  return normalizedPhone;
};

const formatDateTime = (value) => {
  if (!value) return { date: '-', time: '-' };
  const dateValue = moment(value);
  if (!dateValue.isValid()) return { date: '-', time: '-' };
  return {
    date: dateValue.format('DD MMM, YYYY'),
    time: dateValue.format('hh:mm A'),
  };
};

const parseLocation = (location) => {
  if (!location) return { city: '-', state: '-' };
  const parts = String(location)
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return { city: '-', state: '-' };
  if (parts.length === 1) return { city: parts[0], state: '-' };
  return {
    city: parts.slice(0, -1).join(', '),
    state: parts[parts.length - 1],
  };
};

const mapLocationOffer = (offer, index) => {
  const { city, state } = parseLocation(offer?.location);
  const createdAt = formatDateTime(offer?.createdDate);
  const expiryAt = formatDateTime(offer?.expiryDate);
  return {
    id: offer?.offerId ?? index + 1,
    partyName: offer?.seller || 'N/A',
    partyPhone: '',
    createdByName: offer?.createdBy || '-',
    createdByPhone: formatPhoneWithoutCode(
      offer?.createdByCallingCode,
      offer?.createdByPhone,
    ),
    listingId: offer?.offerId ?? '-',
    gradeName: offer?.grade || '-',
    gradeMeta: '',
    city,
    state,
    totalQty: formatQtyWithUnit(offer?.totalQty, QTY_UNIT),
    minimumQty: formatQtyWithUnit(offer?.minimumQty, QTY_UNIT),
    rate: formatRatePerKg(offer?.rate),
    status: offer?.status || '-',
    createdDate: createdAt.date,
    createdTime: createdAt.time,
    expiryDate: expiryAt.date,
    expiryTime: expiryAt.time,
  };
};

const mapLocationDemand = (demand, index) => {
  const { city, state } = parseLocation(demand?.location);
  const createdAt = formatDateTime(demand?.createdDate);
  const expiryAt = formatDateTime(demand?.expiryDate);
  return {
    id: demand?.demandId ?? index + 1,
    partyName: demand?.buyer || 'N/A',
    partyPhone: '',
    createdByName: demand?.createdBy || '-',
    createdByPhone: formatPhoneWithoutCode(
      demand?.createdByCallingCode,
      demand?.createdByPhone,
    ),
    listingId: demand?.demandId ?? '-',
    gradeName: demand?.grade || '-',
    gradeMeta: '',
    city,
    state,
    totalQty: formatQtyWithUnit(demand?.totalQty, QTY_UNIT),
    minimumQty: formatQtyWithUnit(demand?.minimumQty, QTY_UNIT),
    rate: formatRatePerKg(demand?.rate),
    status: demand?.status || '-',
    createdDate: createdAt.date,
    createdTime: createdAt.time,
    expiryDate: expiryAt.date,
    expiryTime: expiryAt.time,
  };
};

// User report mapping
const mapUserOffer = (offer, index) => {
  const { city, state } = parseLocation(offer?.location);
  const createdAt = formatDateTime(offer?.createdDate);
  const expiryAt = formatDateTime(offer?.expiryDate);
  return {
    id: offer?.offerId ?? index + 1,
    partyName: offer?.seller || 'N/A',
    partyPhone: '',
    createdByName: offer?.createdBy || '-',
    createdByPhone: formatPhoneWithoutCode(
      offer?.createdByCallingCode,
      offer?.createdByPhone,
    ),
    listingId: offer?.offerId ?? '-',
    gradeName: offer?.grade || '-',
    gradeMeta: '',
    city,
    state,
    totalQty: formatQtyWithUnit(offer?.totalQty, QTY_UNIT),
    minimumQty: formatQtyWithUnit(offer?.minimumQty, QTY_UNIT),
    rate: formatRatePerKg(offer?.rate),
    status: offer?.status || '-',
    createdDate: createdAt.date,
    createdTime: createdAt.time,
    expiryDate: expiryAt.date,
    expiryTime: expiryAt.time,
  };
};

const mapUserDemand = (demand, index) => {
  const { city, state } = parseLocation(demand?.location);
  const createdAt = formatDateTime(demand?.createdDate);
  const expiryAt = formatDateTime(demand?.expiryDate);
  return {
    id: demand?.demandId ?? index + 1,
    partyName: demand?.buyer || 'N/A',
    partyPhone: '',
    createdByName: demand?.createdBy || '-',
    createdByPhone: formatPhoneWithoutCode(
      demand?.createdByCallingCode,
      demand?.createdByPhone,
    ),
    listingId: demand?.demandId ?? '-',
    gradeName: demand?.grade || '-',
    gradeMeta: '',
    city,
    state,
    totalQty: formatQtyWithUnit(demand?.totalQty, QTY_UNIT),
    minimumQty: formatQtyWithUnit(demand?.minimumQty, QTY_UNIT),
    rate: formatRatePerKg(demand?.rate),
    status: demand?.status || '-',
    createdDate: createdAt.date,
    createdTime: createdAt.time,
    expiryDate: expiryAt.date,
    expiryTime: expiryAt.time,
  };
};

const buildLocationLabel = (row) => {
  const city = row?.city && row.city !== '-' ? row.city : '';
  const state = row?.state && row.state !== '-' ? row.state : '';
  if (city && state) return `${city}, ${state}`;
  return city || state || '';
};

const sortByCreatedDateDesc = (items) => {
  if (!Array.isArray(items)) return [];
  return [...items].sort((a, b) => {
    const left = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
    const right = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
    return right - left;
  });
};

const mergeLocationReports = (reports) => {
  const merged = {
    totalOffers: 0,
    totalOfferQty: 0,
    totalDemands: 0,
    totalDemandQty: 0,
    totalSellers: 0,
    totalBuyers: 0,
    offers: [],
    demands: [],
  };

  (reports || []).filter(Boolean).forEach((report) => {
    merged.totalOffers += Number(report.totalOffers || 0);
    merged.totalOfferQty += Number(report.totalOfferQty || 0);
    merged.totalDemands += Number(report.totalDemands || 0);
    merged.totalDemandQty += Number(report.totalDemandQty || 0);
    merged.totalSellers += Number(report.totalSellers || 0);
    merged.totalBuyers += Number(report.totalBuyers || 0);
    if (Array.isArray(report.offers)) merged.offers.push(...report.offers);
    if (Array.isArray(report.demands)) merged.demands.push(...report.demands);
  });

  merged.offers = sortByCreatedDateDesc(merged.offers);
  merged.demands = sortByCreatedDateDesc(merged.demands);
  return merged;
};

const mergeUserReports = (reports) => {
  const merged = {
    totalOffers: 0,
    totalOfferQty: 0,
    offerQtyUnit: null,
    totalDemands: 0,
    totalDemandQty: 0,
    demandQtyUnit: null,
    offers: [],
    demands: [],
  };

  const list = (reports || []).filter(Boolean);
  merged.offerQtyUnit = list.find((r) => r?.offerQtyUnit)?.offerQtyUnit || null;
  merged.demandQtyUnit =
    list.find((r) => r?.demandQtyUnit)?.demandQtyUnit || null;

  list.forEach((report) => {
    merged.totalOffers += Number(report.totalOffers || 0);
    merged.totalOfferQty += Number(report.totalOfferQty || 0);
    merged.totalDemands += Number(report.totalDemands || 0);
    merged.totalDemandQty += Number(report.totalDemandQty || 0);
    if (Array.isArray(report.offers)) merged.offers.push(...report.offers);
    if (Array.isArray(report.demands)) merged.demands.push(...report.demands);
  });

  merged.offers = sortByCreatedDateDesc(merged.offers);
  merged.demands = sortByCreatedDateDesc(merged.demands);
  return merged;
};

const escapeCsvValue = (value) => {
  const normalized = value === null || value === undefined ? '' : String(value);
  if (/["\n,]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }
  return normalized;
};

const triggerCsvDownload = (csvBody, fileName) => {
  const safeName = fileName || 'report';
  const blob = new Blob([`\uFEFF${csvBody}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${safeName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ─── Component ────────────────────────────────────────────────────────────────

const ReportTab = () => {
  // ── Report type & listing type
  const [reportType, setReportType] = useState('location');
  const [listingType, setListingType] = useState('offer');
  const [expandedMobileCardId, setExpandedMobileCardId] = useState(null);

  // ── Year / Month – store numeric values for easy API use
  const currentDate = new Date();
  const defaultMonthOption =
    months.find((m) => m.value === currentDate.getMonth() + 1) || months[0];
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonths, setSelectedMonths] = useState([defaultMonthOption]);

  // ── State / City
  const [selectedState, setSelectedState] = useState(ALL_STATES_OPTION);
  const [selectedCity, setSelectedCity] = useState(ALL_CITIES_OPTION);
  const [stateOptions, setStateOptions] = useState([ALL_STATES_OPTION]);
  const [cityOptions, setCityOptions] = useState([ALL_CITIES_OPTION]);

  // ── Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(REPORT_PAGE_SIZE);
  const [totalOffers, setTotalOffers] = useState(0);
  const [totalDemands, setTotalDemands] = useState(0);

  // Table filters (dropdown)
  const [openFilter, setOpenFilter] = useState(null); // 'sellerBuyer' | 'createdBy' | 'grade' | 'location'
  const [filterSearchText, setFilterSearchText] = useState({
    sellerBuyer: '',
    createdBy: '',
    grade: '',
    location: '',
  });
  const [selectedFilters, setSelectedFilters] = useState({
    sellerBuyer: [],
    createdBy: [],
    grade: [],
    location: [],
  });
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [filterOptionsError, setFilterOptionsError] = useState(null);
  const filterDropdownRef = useRef(null);
  const previousOpenFilterRef = useRef(null);
  const justClosedFilterRef = useRef(false);

  // ── User / Trader filter
  const [traders, setTraders] = useState([]);
  const [filteredTraders, setFilteredTraders] = useState([]);
  const [userSearchText, setUserSearchText] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [loadingTraders, setLoadingTraders] = useState(false);
  const userDropdownRef = useRef(null);
  const userSearchTimeoutRef = useRef(null);
  const userSearchIdRef = useRef(0);

  // ── Lazy queries for states & cities
  const [fetchStates, { loading: statesLoading }] = useLazyQuery(
    STATES_BY_COUNTRY,
    {
      fetchPolicy: 'network-only',
      onCompleted: (response) => {
        const mappedStates = (response?.statesByCountry || []).map((s) => ({
          value: String(s.id),
          label: s.stateName,
        }));
        setStateOptions([ALL_STATES_OPTION, ...mappedStates]);
      },
      onError: (err) => {
        console.error('Failed to fetch states', err);
        setStateOptions([ALL_STATES_OPTION]);
      },
    },
  );

  const [fetchCities, { loading: citiesLoading }] = useLazyQuery(
    CITY_BY_STATE,
    {
      fetchPolicy: 'network-only',
      onCompleted: (response) => {
        const mappedCities = (response?.citiesByState || []).map((c) => ({
          value: String(c.id),
          label: c.cityName,
        }));
        setCityOptions([ALL_CITIES_OPTION, ...mappedCities]);
      },
      onError: (err) => {
        console.error('Failed to fetch cities', err);
        setCityOptions([ALL_CITIES_OPTION]);
      },
    },
  );

  // ── Apollo client for direct inline queries (avoids Long scalar type mismatch)
  const apolloClient = useApolloClient();

  const fetchLocationReportPage = useCallback(
    async (year, month, stateId, cityId, page, size) => {
      const query = buildLocationReportQuery({
        year,
        month,
        stateId,
        cityId,
        page,
        size,
      });
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only',
      });
      return result.data?.adminLocationReport || null;
    },
    [apolloClient],
  );

  const fetchUserReportPage = useCallback(
    async (year, month, traderId, page, size) => {
      const query = buildUserReportQuery({
        year,
        month,
        traderId,
        page,
        size,
      });
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only',
      });
      return result.data?.adminUserReport || null;
    },
    [apolloClient],
  );

  const fetchFullLocationReportForMonth = useCallback(
    async (year, month, stateId, cityId, size) => {
      const safeSize = Math.max(1, Number(size) || REPORT_PAGE_SIZE);
      const firstReport = await fetchLocationReportPage(
        year,
        month,
        stateId,
        cityId,
        1,
        safeSize,
      );

      if (!firstReport) {
        return {
          totalOffers: 0,
          totalOfferQty: 0,
          totalDemands: 0,
          totalDemandQty: 0,
          totalSellers: 0,
          totalBuyers: 0,
          offers: [],
          demands: [],
        };
      }

      const monthTotalOffers = firstReport.totalOffers || 0;
      const monthTotalOfferQty = firstReport.totalOfferQty || 0;
      const monthTotalDemands = firstReport.totalDemands || 0;
      const monthTotalDemandQty = firstReport.totalDemandQty || 0;
      const monthTotalSellers = firstReport.totalSellers || 0;
      const monthTotalBuyers = firstReport.totalBuyers || 0;

      const offerPages = Math.ceil((Number(monthTotalOffers) || 0) / safeSize);
      const demandPages = Math.ceil(
        (Number(monthTotalDemands) || 0) / safeSize,
      );
      const totalPages = Math.max(offerPages, demandPages, 1);

      const pagePromises = [];
      for (let page = 2; page <= totalPages; page += 1) {
        pagePromises.push(
          fetchLocationReportPage(year, month, stateId, cityId, page, safeSize),
        );
      }

      const remainingReports = pagePromises.length
        ? (await Promise.all(pagePromises)).filter(Boolean)
        : [];

      const allReports = [firstReport, ...remainingReports];
      const offers = [];
      const demands = [];
      allReports.forEach((report) => {
        if (Array.isArray(report.offers)) offers.push(...report.offers);
        if (Array.isArray(report.demands)) demands.push(...report.demands);
      });

      return {
        totalOffers: monthTotalOffers,
        totalOfferQty: monthTotalOfferQty,
        totalDemands: monthTotalDemands,
        totalDemandQty: monthTotalDemandQty,
        totalSellers: monthTotalSellers,
        totalBuyers: monthTotalBuyers,
        offers,
        demands,
      };
    },
    [fetchLocationReportPage],
  );

  const fetchFullUserReportForMonth = useCallback(
    async (year, month, traderId, size) => {
      const safeSize = Math.max(1, Number(size) || REPORT_PAGE_SIZE);
      const firstReport = await fetchUserReportPage(
        year,
        month,
        traderId,
        1,
        safeSize,
      );

      if (!firstReport) {
        return {
          totalOffers: 0,
          totalOfferQty: 0,
          offerQtyUnit: null,
          totalDemands: 0,
          totalDemandQty: 0,
          demandQtyUnit: null,
          offers: [],
          demands: [],
        };
      }

      const monthTotalOffers = firstReport.totalOffers || 0;
      const monthTotalOfferQty = firstReport.totalOfferQty || 0;
      const monthOfferQtyUnit = firstReport.offerQtyUnit || null;
      const monthTotalDemands = firstReport.totalDemands || 0;
      const monthTotalDemandQty = firstReport.totalDemandQty || 0;
      const monthDemandQtyUnit = firstReport.demandQtyUnit || null;

      const offerPages = Math.ceil((Number(monthTotalOffers) || 0) / safeSize);
      const demandPages = Math.ceil(
        (Number(monthTotalDemands) || 0) / safeSize,
      );
      const totalPages = Math.max(offerPages, demandPages, 1);

      const pagePromises = [];
      for (let page = 2; page <= totalPages; page += 1) {
        pagePromises.push(
          fetchUserReportPage(year, month, traderId, page, safeSize),
        );
      }

      const remainingReports = pagePromises.length
        ? (await Promise.all(pagePromises)).filter(Boolean)
        : [];

      const allReports = [firstReport, ...remainingReports];
      const offers = [];
      const demands = [];
      allReports.forEach((report) => {
        if (Array.isArray(report.offers)) offers.push(...report.offers);
        if (Array.isArray(report.demands)) demands.push(...report.demands);
      });

      return {
        totalOffers: monthTotalOffers,
        totalOfferQty: monthTotalOfferQty,
        offerQtyUnit: monthOfferQtyUnit,
        totalDemands: monthTotalDemands,
        totalDemandQty: monthTotalDemandQty,
        demandQtyUnit: monthDemandQtyUnit,
        offers,
        demands,
      };
    },
    [fetchUserReportPage],
  );

  // ── Location report state
  const [locationReportData, setLocationReportData] = useState(null);
  const [locationReportLoading, setLocationReportLoading] = useState(false);
  const [locationReportError, setLocationReportError] = useState(null);

  // User report state
  const [userReportData, setUserReportData] = useState(null);
  const [userReportLoading, setUserReportLoading] = useState(false);
  const [userReportError, setUserReportError] = useState(null);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [csvError, setCsvError] = useState(null);

  const selectedMonthValues = useMemo(
    () =>
      (selectedMonths || [])
        .map((m) => Number(m?.value))
        .filter((m) => Number.isFinite(m)),
    [selectedMonths],
  );
  const isMultiMonth = selectedMonthValues.length > 1;
  const isAllStatesSelected = selectedState.value === 'ALL';
  const sellerBuyerText = (selectedFilters.sellerBuyer || []).length;
  const createdByText = (selectedFilters.createdBy || []).length;
  const gradeText = (selectedFilters.grade || []).length;
  const locationText = (selectedFilters.location || []).length;
  const isSearchActive =
    (reportType === 'location' && sellerBuyerText > 0) ||
    createdByText > 0 ||
    gradeText > 0 ||
    locationText > 0;
  const isFullDataMode = isSearchActive || Boolean(openFilter);

  const hasUserSelected = Boolean(selectedUser?.id);

  useEffect(() => {
    if (previousOpenFilterRef.current && !openFilter) {
      justClosedFilterRef.current = true;
    }
    previousOpenFilterRef.current = openFilter;
  }, [openFilter]);

  // ── Fetch location report
  const fetchLocationReport = useCallback(
    async (
      year,
      monthSelection,
      stateId,
      cityId,
      page = 1,
      size = REPORT_PAGE_SIZE,
      forceFullFetch = false,
      options = {},
    ) => {
      const { suppressLoading = false } = options;
      const monthValues = Array.isArray(monthSelection)
        ? monthSelection
        : [monthSelection];
      const safeMonths = monthValues
        .map((m) => Number(m))
        .filter((m) => Number.isFinite(m));
      if (safeMonths.length === 0) {
        setLocationReportData(null);
        setTotalOffers(0);
        setTotalDemands(0);
        return;
      }

      if (suppressLoading) {
        setFilterOptionsLoading(true);
        setFilterOptionsError(null);
      } else {
        setLocationReportLoading(true);
        setLocationReportError(null);
      }
      try {
        const shouldFetchFull = forceFullFetch || safeMonths.length > 1;
        if (!shouldFetchFull) {
          const query = buildLocationReportQuery({
            year,
            month: safeMonths[0],
            stateId,
            cityId,
            page,
            size,
          });
          const result = await apolloClient.query({
            query,
            fetchPolicy: 'network-only',
          });
          setLocationReportData(result.data);
          const report = result.data?.adminLocationReport;
          if (report) {
            setTotalOffers(report.totalOffers || 0);
            setTotalDemands(report.totalDemands || 0);
          }
        } else {
          const fetchSize = Math.max(size, REPORT_PAGE_SIZE);
          const reports =
            safeMonths.length === 1
              ? [
                  await fetchFullLocationReportForMonth(
                    year,
                    safeMonths[0],
                    stateId,
                    cityId,
                    fetchSize,
                  ),
                ]
              : await Promise.all(
                  safeMonths.map((month) =>
                    fetchFullLocationReportForMonth(
                      year,
                      month,
                      stateId,
                      cityId,
                      fetchSize,
                    ),
                  ),
                );
          const merged = mergeLocationReports(reports);
          setLocationReportData({ adminLocationReport: merged });
          setTotalOffers(merged.totalOffers || 0);
          setTotalDemands(merged.totalDemands || 0);
        }
      } catch (err) {
        console.error('Location report fetch error', err);
        if (suppressLoading) {
          setFilterOptionsError(err);
        } else {
          setLocationReportError(err);
        }
      } finally {
        if (suppressLoading) {
          setFilterOptionsLoading(false);
        } else {
          setLocationReportLoading(false);
        }
      }
    },
    [apolloClient, fetchFullLocationReportForMonth],
  );

  // Fetch user report
  const fetchUserReport = useCallback(
    async (
      year,
      monthSelection,
      traderId,
      page = 1,
      size = REPORT_PAGE_SIZE,
      forceFullFetch = false,
      options = {},
    ) => {
      const { suppressLoading = false } = options;
      const numericTraderId = Number(traderId);
      if (!Number.isFinite(numericTraderId)) return;
      const monthValues = Array.isArray(monthSelection)
        ? monthSelection
        : [monthSelection];
      const safeMonths = monthValues
        .map((m) => Number(m))
        .filter((m) => Number.isFinite(m));
      if (safeMonths.length === 0) {
        setUserReportData(null);
        return;
      }

      if (suppressLoading) {
        setFilterOptionsLoading(true);
        setFilterOptionsError(null);
      } else {
        setUserReportLoading(true);
        setUserReportError(null);
      }
      try {
        const shouldFetchFull = forceFullFetch || safeMonths.length > 1;
        if (!shouldFetchFull) {
          const query = buildUserReportQuery({
            year,
            month: safeMonths[0],
            traderId: numericTraderId,
            page,
            size,
          });
          const result = await apolloClient.query({
            query,
            fetchPolicy: 'network-only',
          });
          setUserReportData(result.data);
        } else {
          const fetchSize = Math.max(size, REPORT_PAGE_SIZE);
          const reports =
            safeMonths.length === 1
              ? [
                  await fetchFullUserReportForMonth(
                    year,
                    safeMonths[0],
                    numericTraderId,
                    fetchSize,
                  ),
                ]
              : await Promise.all(
                  safeMonths.map((month) =>
                    fetchFullUserReportForMonth(
                      year,
                      month,
                      numericTraderId,
                      fetchSize,
                    ),
                  ),
                );
          const merged = mergeUserReports(reports);
          setUserReportData({ adminUserReport: merged });
        }
      } catch (err) {
        console.error('User report fetch error', err);
        if (suppressLoading) {
          setFilterOptionsError(err);
        } else {
          setUserReportError(err);
        }
      } finally {
        if (suppressLoading) {
          setFilterOptionsLoading(false);
        } else {
          setUserReportLoading(false);
        }
      }
    },
    [apolloClient, fetchFullUserReportForMonth],
  );

  // ── Re-fetch when filters change — reset to page 1
  useEffect(() => {
    if (reportType !== 'location') return;
    setCurrentPage(1);
    const stateId = isAllStatesSelected
      ? null
      : parseInt(selectedState.value, 10);
    const cityId =
      isAllStatesSelected || selectedCity.value === 'ALL'
        ? null
        : parseInt(selectedCity.value, 10);
    const suppressLoading = isFullDataMode || justClosedFilterRef.current;
    let isActive = true;
    const run = async () => {
      await fetchLocationReport(
        selectedYear,
        selectedMonthValues,
        stateId,
        cityId,
        1,
        pageSize,
        isFullDataMode,
        { suppressLoading },
      );
      if (isActive) {
        justClosedFilterRef.current = false;
      }
    };
    run();
    return () => {
      isActive = false;
    };
  }, [
    reportType,
    selectedYear,
    selectedMonthValues,
    selectedState,
    selectedCity,
    isAllStatesSelected,
    pageSize,
    isFullDataMode,
    fetchLocationReport,
  ]);

  // Re-fetch when user filters change - reset to page 1
  useEffect(() => {
    if (reportType !== 'user' || !hasUserSelected) return;
    setCurrentPage(1);
    const suppressLoading = isFullDataMode || justClosedFilterRef.current;
    let isActive = true;
    const run = async () => {
      await fetchUserReport(
        selectedYear,
        selectedMonthValues,
        selectedUser.id,
        1,
        pageSize,
        isFullDataMode,
        { suppressLoading },
      );
      if (isActive) {
        justClosedFilterRef.current = false;
      }
    };
    run();
    return () => {
      isActive = false;
    };
  }, [
    reportType,
    selectedYear,
    selectedMonthValues,
    selectedUser,
    hasUserSelected,
    pageSize,
    isFullDataMode,
    fetchUserReport,
  ]);

  // Re-fetch user report when page changes
  const isFirstUserRender = useRef(true);
  useEffect(() => {
    if (isFirstUserRender.current) {
      isFirstUserRender.current = false;
      return;
    }
    if (
      reportType !== 'user' ||
      !hasUserSelected ||
      isMultiMonth ||
      isFullDataMode
    )
      return;
    fetchUserReport(
      selectedYear,
      selectedMonthValues,
      selectedUser.id,
      currentPage,
      pageSize,
    );
  }, [
    currentPage,
    isMultiMonth,
    pageSize,
    reportType,
    selectedMonthValues,
    selectedUser,
    selectedYear,
    hasUserSelected,
    fetchUserReport,
  ]);

  // ── Re-fetch when page changes (but not when filters change — handled above)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (reportType !== 'location' || isMultiMonth || isFullDataMode) return;
    const stateId = isAllStatesSelected
      ? null
      : parseInt(selectedState.value, 10);
    const cityId =
      isAllStatesSelected || selectedCity.value === 'ALL'
        ? null
        : parseInt(selectedCity.value, 10);
    fetchLocationReport(
      selectedYear,
      selectedMonthValues,
      stateId,
      cityId,
      currentPage,
      pageSize,
    );
  }, [
    currentPage,
    isMultiMonth,
    pageSize,
    reportType,
    selectedMonthValues,
    selectedState,
    selectedCity,
    isAllStatesSelected,
    selectedYear,
    fetchLocationReport,
  ]);

  // ── Load traders once on mount
  useEffect(() => {
    const loadTraders = async () => {
      try {
        setLoadingTraders(true);
        const data = await fetchTraders();
        setTraders(data);
        setFilteredTraders(data);
      } catch (error) {
        console.error('Failed to fetch traders', error);
      } finally {
        setLoadingTraders(false);
      }
    };
    loadTraders();
  }, []);

  // ── Load states on mount
  useEffect(() => {
    fetchStates({ variables: { country: INDIA_COUNTRY_ID } });
  }, [fetchStates]);

  // ── Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(e.target)
      ) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (userSearchTimeoutRef.current) {
        clearTimeout(userSearchTimeoutRef.current);
      }
    };
  }, []);

  // ── Close column filter dropdown when clicking outside
  useEffect(() => {
    if (!openFilter) return undefined;
    const handleClickOutside = (e) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(e.target)
      ) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilter]);

  useEffect(() => {
    if (reportType === 'user' && openFilter === 'sellerBuyer') {
      setOpenFilter(null);
    }
  }, [openFilter, reportType]);

  useEffect(() => {
    setCsvError(null);
  }, [
    listingType,
    reportType,
    selectedCity,
    selectedMonthValues,
    selectedState,
    selectedUser,
    selectedYear,
  ]);

  // ── Trader helper fns
  const getTraderCompanyName = (trader) =>
    trader?.entityName ||
    trader?.tradeName ||
    `${trader?.firstName || ''} ${trader?.lastName || ''}`.trim() ||
    'N/A';

  const getTraderContactName = (trader) =>
    `${trader?.firstName || ''} ${trader?.lastName || ''}`.trim() || 'N/A';

  const getTraderPhone = (trader) => {
    const code = trader?.callingCode?.callingCode || '';
    const phone = trader?.phone || '';
    if (!code && !phone) return '';
    if (code && phone) return `${code}-${phone}`;
    return code || phone;
  };

  // ── User search
  const handleUserSearch = (e) => {
    const { value } = e.target;
    setUserSearchText(value);
    setShowUserDropdown(true);

    if (userSearchTimeoutRef.current) {
      clearTimeout(userSearchTimeoutRef.current);
    }

    const nextValue = value;
    if (!nextValue) {
      setFilteredTraders(traders);
    }

    userSearchTimeoutRef.current = setTimeout(async () => {
      userSearchIdRef.current += 1;
      const requestId = userSearchIdRef.current;
      try {
        setLoadingTraders(true);
        const results = await fetchTraders(nextValue);
        if (userSearchIdRef.current !== requestId) return;
        setFilteredTraders(Array.isArray(results) ? results : []);
        if (!nextValue) {
          setTraders(Array.isArray(results) ? results : []);
        }
      } catch (error) {
        if (userSearchIdRef.current !== requestId) return;
        console.error('Failed to fetch traders', error);
        setFilteredTraders([]);
      } finally {
        if (userSearchIdRef.current === requestId) {
          setLoadingTraders(false);
        }
      }
    }, 300);
  };

  const selectUser = (trader) => {
    setSelectedUser(trader);
    setUserSearchText(getTraderCompanyName(trader));
    setShowUserDropdown(false);
  };

  // ── State change → reset city → fetch cities
  const handleStateChange = (option) => {
    const nextState = option || ALL_STATES_OPTION;
    setSelectedState(nextState);
    setSelectedCity(ALL_CITIES_OPTION);
    setCityOptions([ALL_CITIES_OPTION]);

    if (nextState.value === 'ALL') return;

    fetchCities({ variables: { state: nextState.value } });
  };

  // ── Derived rows
  const locationReport = locationReportData?.adminLocationReport;

  const locationRows = useMemo(() => {
    if (!locationReport) return [];
    const rows =
      listingType === 'offer' ? locationReport.offers : locationReport.demands;
    if (!Array.isArray(rows)) return [];
    return listingType === 'offer'
      ? rows.map(mapLocationOffer)
      : rows.map(mapLocationDemand);
  }, [listingType, locationReport]);

  const userReport = userReportData?.adminUserReport;

  const userRows = useMemo(() => {
    if (!userReport) return [];
    const rows =
      listingType === 'offer' ? userReport.offers : userReport.demands;
    if (!Array.isArray(rows)) return [];
    return listingType === 'offer'
      ? rows.map((row, index) => mapUserOffer(row, index))
      : rows.map((row, index) => mapUserDemand(row, index));
  }, [listingType, userReport]);

  const currentRows = useMemo(
    () => (reportType === 'location' ? locationRows : userRows),
    [locationRows, reportType, userRows],
  );

  const filterOptions = useMemo(() => {
    const sellerBuyerSet = new Set();
    const createdBySet = new Set();
    const gradeSet = new Set();
    const locationSet = new Set();
    currentRows.forEach((row) => {
      if (row?.partyName && row.partyName !== '-')
        sellerBuyerSet.add(row.partyName);
      if (row?.createdByName && row.createdByName !== '-')
        createdBySet.add(row.createdByName);
      if (row?.gradeName && row.gradeName !== '-') gradeSet.add(row.gradeName);
      const locationLabel = buildLocationLabel(row);
      if (locationLabel) locationSet.add(locationLabel);
    });
    const sortAlpha = (a, b) => String(a).localeCompare(String(b));
    return {
      sellerBuyer: Array.from(sellerBuyerSet).sort(sortAlpha),
      createdBy: Array.from(createdBySet).sort(sortAlpha),
      grade: Array.from(gradeSet).sort(sortAlpha),
      location: Array.from(locationSet).sort(sortAlpha),
    };
  }, [currentRows]);

  const filteredRows = useMemo(() => {
    if (!isSearchActive) return currentRows;
    const sellerBuyerSet = new Set(
      (selectedFilters.sellerBuyer || []).map((value) =>
        String(value).toLowerCase(),
      ),
    );
    const createdBySet = new Set(
      (selectedFilters.createdBy || []).map((value) =>
        String(value).toLowerCase(),
      ),
    );
    const gradeSet = new Set(
      (selectedFilters.grade || []).map((value) => String(value).toLowerCase()),
    );
    const locationSet = new Set(
      (selectedFilters.location || []).map((value) =>
        String(value).toLowerCase(),
      ),
    );
    return currentRows.filter((row) => {
      const sellerBuyerValue = (row.partyName || '').toLowerCase();
      const createdByValue = (row.createdByName || '').toLowerCase();
      const gradeValue = (row.gradeName || '').toLowerCase();
      const locationValue = buildLocationLabel(row).toLowerCase();

      const sellerBuyerMatch =
        reportType === 'user' || sellerBuyerSet.size === 0
          ? true
          : sellerBuyerSet.has(sellerBuyerValue);
      const createdByMatch =
        createdBySet.size === 0 ? true : createdBySet.has(createdByValue);
      const gradeMatch = gradeSet.size === 0 ? true : gradeSet.has(gradeValue);
      const locationMatch =
        locationSet.size === 0 ? true : locationSet.has(locationValue);

      return sellerBuyerMatch && createdByMatch && gradeMatch && locationMatch;
    });
  }, [currentRows, isSearchActive, reportType, selectedFilters]);

  const isOffer = listingType === 'offer';
  const shouldClientPaginate = isMultiMonth || isFullDataMode;
  const pagedRows = useMemo(() => {
    if (!shouldClientPaginate) return filteredRows;
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [currentPage, filteredRows, pageSize, shouldClientPaginate]);

  const applyFiltersToRows = useCallback(
    (rows) => {
      if (!isSearchActive) return rows;
      const sellerBuyerSet = new Set(
        (selectedFilters.sellerBuyer || []).map((value) =>
          String(value).toLowerCase(),
        ),
      );
      const createdBySet = new Set(
        (selectedFilters.createdBy || []).map((value) =>
          String(value).toLowerCase(),
        ),
      );
      const gradeSet = new Set(
        (selectedFilters.grade || []).map((value) =>
          String(value).toLowerCase(),
        ),
      );
      const locationSet = new Set(
        (selectedFilters.location || []).map((value) =>
          String(value).toLowerCase(),
        ),
      );
      return rows.filter((row) => {
        const sellerBuyerValue = (row.partyName || '').toLowerCase();
        const createdByValue = (row.createdByName || '').toLowerCase();
        const gradeValue = (row.gradeName || '').toLowerCase();
        const locationValue = buildLocationLabel(row).toLowerCase();

        const sellerBuyerMatch =
          reportType === 'user' || sellerBuyerSet.size === 0
            ? true
            : sellerBuyerSet.has(sellerBuyerValue);
        const createdByMatch =
          createdBySet.size === 0 ? true : createdBySet.has(createdByValue);
        const gradeMatch =
          gradeSet.size === 0 ? true : gradeSet.has(gradeValue);
        const locationMatch =
          locationSet.size === 0 ? true : locationSet.has(locationValue);

        return (
          sellerBuyerMatch && createdByMatch && gradeMatch && locationMatch
        );
      });
    },
    [isSearchActive, reportType, selectedFilters],
  );

  const buildCsvHeaders = useCallback(() => {
    const headers = [
      'S.NO.',
      isOffer ? 'SELLER' : 'BUYER',
      'CREATED BY',
      isOffer ? 'OFFER ID' : 'DEMAND ID',
      'GRADE',
      'LOCATION',
      'TOTAL QTY',
      'MIN. QTY',
      'RATE',
      'STATUS',
    ];
    if (isOffer) headers.push('CREATED DATE');
    headers.push('EXPIRY DATE');
    return headers;
  }, [isOffer]);

  const buildCsvRow = useCallback(
    (row) => {
      const createdDateTime =
        row.createdDate && row.createdTime
          ? `${row.createdDate} ${row.createdTime}`
          : row.createdDate || '';
      const expiryDateTime =
        row.expiryDate && row.expiryTime
          ? `${row.expiryDate} ${row.expiryTime}`
          : row.expiryDate || '';
      const values = [
        row.id ?? '',
        row.partyName ?? '',
        row.createdByName ?? '',
        row.listingId ?? '',
        row.gradeName ?? '',
        buildLocationLabel(row),
        row.totalQty ?? '',
        row.minimumQty ?? '',
        row.rate ?? '',
        row.status ?? '',
      ];
      if (isOffer) values.push(createdDateTime);
      values.push(expiryDateTime);
      return values;
    },
    [isOffer],
  );

  const buildCsvFileName = useCallback(() => {
    const monthToken =
      selectedMonthValues.length > 0
        ? selectedMonthValues.join('-')
        : 'all-months';
    const rawName = `report-${reportType}-${listingType}-${selectedYear}-${monthToken}`;
    return rawName.replace(/[^a-zA-Z0-9-_]/g, '');
  }, [listingType, reportType, selectedMonthValues, selectedYear]);

  const getLocationReportForDownload = useCallback(async () => {
    const stateId = isAllStatesSelected
      ? null
      : parseInt(selectedState.value, 10);
    const cityId =
      isAllStatesSelected || selectedCity.value === 'ALL'
        ? null
        : parseInt(selectedCity.value, 10);
    const monthsToFetch =
      selectedMonthValues.length > 0 ? selectedMonthValues : [];
    if (monthsToFetch.length === 0) return null;
    const fetchSize = Math.max(pageSize, REPORT_PAGE_SIZE);
    const reports =
      monthsToFetch.length === 1
        ? [
            await fetchFullLocationReportForMonth(
              selectedYear,
              monthsToFetch[0],
              stateId,
              cityId,
              fetchSize,
            ),
          ]
        : await Promise.all(
            monthsToFetch.map((month) =>
              fetchFullLocationReportForMonth(
                selectedYear,
                month,
                stateId,
                cityId,
                fetchSize,
              ),
            ),
          );
    return mergeLocationReports(reports);
  }, [
    fetchFullLocationReportForMonth,
    isAllStatesSelected,
    pageSize,
    selectedCity,
    selectedMonthValues,
    selectedState,
    selectedYear,
  ]);

  const getUserReportForDownload = useCallback(async () => {
    if (!hasUserSelected) return null;
    const traderId = Number(selectedUser?.id);
    if (!Number.isFinite(traderId)) return null;
    const monthsToFetch =
      selectedMonthValues.length > 0 ? selectedMonthValues : [];
    if (monthsToFetch.length === 0) return null;
    const fetchSize = Math.max(pageSize, REPORT_PAGE_SIZE);
    const reports =
      monthsToFetch.length === 1
        ? [
            await fetchFullUserReportForMonth(
              selectedYear,
              monthsToFetch[0],
              traderId,
              fetchSize,
            ),
          ]
        : await Promise.all(
            monthsToFetch.map((month) =>
              fetchFullUserReportForMonth(
                selectedYear,
                month,
                traderId,
                fetchSize,
              ),
            ),
          );
    return mergeUserReports(reports);
  }, [
    fetchFullUserReportForMonth,
    hasUserSelected,
    pageSize,
    selectedMonthValues,
    selectedUser,
    selectedYear,
  ]);

  const handleDownloadCsv = useCallback(async () => {
    if (csvDownloading) return;
    if (reportType === 'user' && !hasUserSelected) return;
    setCsvDownloading(true);
    setCsvError(null);
    try {
      let reportData = null;
      if (reportType === 'location') {
        reportData = await getLocationReportForDownload();
      } else {
        reportData = await getUserReportForDownload();
      }

      if (!reportData) {
        setCsvError('No data available for download.');
        return;
      }

      const sourceRows = isOffer
        ? reportData.offers || []
        : reportData.demands || [];

      const mappedRows =
        reportType === 'location'
          ? sourceRows.map((row, index) =>
              isOffer
                ? mapLocationOffer(row, index)
                : mapLocationDemand(row, index),
            )
          : sourceRows.map((row, index) =>
              isOffer ? mapUserOffer(row, index) : mapUserDemand(row, index),
            );

      const filteredDownloadRows = applyFiltersToRows(mappedRows);
      const headers = buildCsvHeaders();
      const csvBody = [
        headers.join(','),
        ...filteredDownloadRows.map((row) =>
          buildCsvRow(row).map(escapeCsvValue).join(','),
        ),
      ].join('\n');

      triggerCsvDownload(csvBody, buildCsvFileName());
    } catch (err) {
      console.error('CSV download failed', err);
      setCsvError(err?.message || 'CSV download failed.');
    } finally {
      setCsvDownloading(false);
    }
  }, [
    applyFiltersToRows,
    buildCsvFileName,
    buildCsvHeaders,
    buildCsvRow,
    csvDownloading,
    getLocationReportForDownload,
    getUserReportForDownload,
    hasUserSelected,
    isOffer,
    reportType,
  ]);

  // ── Column filter helpers
  // ✅ FIXED: Moved these three functions ABOVE renderFilterDropdown and renderFilterHeader
  //    to resolve the no-use-before-define ESLint errors.

  const toggleFilterValue = (key, value) => {
    setSelectedFilters((prev) => {
      const nextValues = new Set(prev[key] || []);
      if (nextValues.has(value)) {
        nextValues.delete(value);
      } else {
        nextValues.add(value);
      }
      return { ...prev, [key]: Array.from(nextValues) };
    });
    setCurrentPage(1);
  };

  const updateFilterSearchText = (key, value) => {
    setFilterSearchText((prev) => ({ ...prev, [key]: value }));
  };

  const getFilteredOptions = useCallback(
    (key) => {
      const searchValue = (filterSearchText[key] || '').trim().toLowerCase();
      const options = filterOptions[key] || [];
      if (!searchValue) return options;
      return options.filter((option) =>
        String(option).toLowerCase().includes(searchValue),
      );
    },
    [filterOptions, filterSearchText],
  );

  // ── UI helpers
  const listingTabs = ['Offer', 'Demand'];

  const selectMenuStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  };
  const menuPortalTarget = typeof window === 'undefined' ? null : document.body;

  let cityPlaceholder = 'All Cities';
  if (citiesLoading) cityPlaceholder = 'Loading cities...';

  // Summary cards
  const getLocationSummary = (valueBuilder) => {
    if (locationReportError) return 'Error';
    if (locationReportLoading) return 'Loading...';
    return valueBuilder();
  };

  const getUserSummary = (valueBuilder, emptyText = 'Select a User') => {
    if (!hasUserSelected) return emptyText;
    if (userReportError) return 'Error';
    if (userReportLoading) return 'Loading...';
    return valueBuilder();
  };

  const locationOfferSummary = getLocationSummary(() =>
    formatCountWithQty(
      locationReport?.totalOffers,
      locationReport?.totalOfferQty,
    ),
  );

  const locationDemandSummary = getLocationSummary(() =>
    formatCountWithQty(
      locationReport?.totalDemands,
      locationReport?.totalDemandQty,
    ),
  );

  const locationSellerSummary = getLocationSummary(
    () => formatNumber(locationReport?.totalSellers),
    '-',
  );

  const locationBuyerSummary = getLocationSummary(
    () => formatNumber(locationReport?.totalBuyers),
    '-',
  );

  const userOfferSummary = getUserSummary(() =>
    formatCountWithQty(
      userReport?.totalOffers,
      userReport?.totalOfferQty,
      QTY_UNIT,
    ),
  );

  const userDemandSummary = getUserSummary(() =>
    formatCountWithQty(
      userReport?.totalDemands,
      userReport?.totalDemandQty,
      QTY_UNIT,
    ),
  );

  const downloadTotalCount =
    reportType === 'location'
      ? isOffer
        ? locationReport?.totalOffers
        : locationReport?.totalDemands
      : isOffer
        ? userReport?.totalOffers
        : userReport?.totalDemands;
  const hasDownloadData = isSearchActive
    ? filteredRows.length > 0
    : Number(downloadTotalCount || 0) > 0;
  const downloadDisabled =
    csvDownloading ||
    (reportType === 'user' && !hasUserSelected) ||
    (reportType === 'location'
      ? locationReportLoading || locationReportError
      : userReportLoading || userReportError) ||
    !hasDownloadData;

  const reportTableColSpan = isOffer ? 12 : 11;

  const renderFilterDropdown = (key, label) => {
    const options = getFilteredOptions(key);
    return (
      <div className="report-filter-dropdown">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder={`Search ${label}`}
          value={filterSearchText[key]}
          onChange={(e) => updateFilterSearchText(key, e.target.value)}
          disabled={filterOptionsLoading}
        />
        {filterOptionsLoading ? (
          <div className="report-filter-loading">
            <Spinner color="primary" size="sm" />
            <span>Loading filters...</span>
          </div>
        ) : (
          <div className="report-filter-options">
            {filterOptionsError && (
              <div className="report-filter-empty">Failed to load filters.</div>
            )}
            {!filterOptionsError && options.length === 0 && (
              <div className="report-filter-empty">No results</div>
            )}
            {!filterOptionsError &&
              options.map((option) => {
                const selected = (selectedFilters[key] || []).includes(option);
                return (
                  <label
                    className="report-filter-option"
                    key={`${key}-${option}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleFilterValue(key, option)}
                    />
                    <span>{option}</span>
                  </label>
                );
              })}
          </div>
        )}
      </div>
    );
  };

  const renderFilterHeader = (key, label, disabled = false) => {
    const isOpen = openFilter === key;
    return (
      <div
        className={`report-filter-header ${disabled ? 'is-disabled' : ''}`}
        ref={isOpen ? filterDropdownRef : null}
      >
        <button
          type="button"
          className="report-filter-trigger"
          onClick={() => {
            if (disabled) return;
            setOpenFilter(isOpen ? null : key);
          }}
        >
          <span>{label}</span>
          {!disabled && <span className="report-filter-caret">▾</span>}
        </button>
        {isOpen && !disabled && renderFilterDropdown(key, label)}
      </div>
    );
  };

  const renderTableRows = () => {
    if (reportType === 'user' && !hasUserSelected) {
      return (
        <tr>
          <td
            colSpan={reportTableColSpan}
            style={{ textAlign: 'center', color: '#888', padding: '24px' }}
          >
            Please select a User to view the report.
          </td>
        </tr>
      );
    }

    if (reportType === 'location' && locationReportError) {
      return (
        <tr>
          <td colSpan={reportTableColSpan}>Failed to load report data.</td>
        </tr>
      );
    }

    if (reportType === 'user' && userReportError) {
      return (
        <tr>
          <td colSpan={reportTableColSpan}>Failed to load report data.</td>
        </tr>
      );
    }

    if (reportType === 'location' && locationReportLoading) {
      return (
        <tr>
          <td colSpan={reportTableColSpan}>Loading...</td>
        </tr>
      );
    }

    if (reportType === 'user' && userReportLoading) {
      return (
        <tr>
          <td colSpan={reportTableColSpan}>Loading...</td>
        </tr>
      );
    }

    if (filteredRows.length === 0) {
      return (
        <tr>
          <td colSpan={reportTableColSpan}>No records found.</td>
        </tr>
      );
    }

    return pagedRows.map((row) => (
      <tr key={`${listingType}-${row.id}`}>
        <td>{row.id}</td>
        <td>
          <div className="report-user-name">{row.partyName}</div>
          <div className="report-user-sub">{row.partyPhone}</div>
        </td>
        <td>
          {(() => {
            const safeName =
              row.createdByName && row.createdByName !== '-'
                ? row.createdByName
                : '';
            const safePhone = row.createdByPhone || '';
            if (!safeName && !safePhone) return '-';
            return (
              <div className="report-user-name">
                {safeName && <span>{safeName}</span>}
                {safePhone && (
                  <span className="report-user-phone-inline">
                    {safeName ? ` ${safePhone}` : safePhone}
                  </span>
                )}
              </div>
            );
          })()}
        </td>
        <td>
          <span className="report-link-text">{row.listingId}</span>
        </td>
        <td>
          <span className="report-grade-badge">{row.gradeName}</span>
          <div className="report-user-sub">{row.gradeMeta}</div>
        </td>
        <td>
          <div>{row.city}</div>
          <div className="report-user-sub">{row.state}</div>
        </td>
        <td>{row.totalQty}</td>
        <td>{row.minimumQty}</td>
        <td>{row.rate}</td>
        <td>
          <span className="report-status-pill">{row.status}</span>
        </td>
        {isOffer && (
          <td>
            <div>{row.createdDate}</div>
            <div className="report-user-sub">{row.createdTime}</div>
          </td>
        )}
        <td>
          <div>{row.expiryDate}</div>
          <div className="report-user-sub">{row.expiryTime}</div>
        </td>
      </tr>
    ));
  };

  const renderMobileRows = () => {
    if (reportType === 'user' && !hasUserSelected) {
      return (
        <div className="analytics-mobile-empty">
          Please select a User to view the report.
        </div>
      );
    }

    if (
      (reportType === 'location' && locationReportError) ||
      (reportType === 'user' && userReportError)
    ) {
      return (
        <div className="analytics-mobile-empty">
          Failed to load report data.
        </div>
      );
    }

    if (
      (reportType === 'location' && locationReportLoading) ||
      (reportType === 'user' && userReportLoading)
    ) {
      return <div className="analytics-mobile-empty">Loading...</div>;
    }

    if (filteredRows.length === 0) {
      return <div className="analytics-mobile-empty">No records found.</div>;
    }

    return pagedRows.map((row) => {
      const cardId = `${listingType}-${row.id}`;
      const isExpanded = expandedMobileCardId === cardId;
      return (
        <div
          className="analytics-mobile-card report-mobile-card"
          key={`mobile-${cardId}`}
        >
          <div className="analytics-mobile-card__summary">
            <div className="analytics-mobile-card__identity">
              <div className="analytics-mobile-card__name">{row.partyName}</div>
              <div className="analytics-mobile-card__meta">
                {`ID ${row.id} | ${row.partyPhone || '-'}`}
              </div>
            </div>
            <div className="analytics-mobile-card__aside">
              <span className="analytics-mobile-card__pill warning">
                {row.status}
              </span>
              <span className="analytics-mobile-card__pill muted">
                {isOffer ? 'Offer' : 'Demand'} {row.listingId}
              </span>
              <button
                type="button"
                className={`analytics-mobile-card__expand-toggle ${
                  isExpanded ? 'is-open' : ''
                }`}
                onClick={() => {
                  setExpandedMobileCardId((prevId) => {
                    return prevId === cardId ? null : cardId;
                  });
                }}
                aria-expanded={isExpanded}
                aria-label={
                  isExpanded
                    ? 'Collapse report details'
                    : 'Expand report details'
                }
              >
                <FaChevronDown />
              </button>
            </div>
          </div>

          {isExpanded ? (
            <div className="analytics-mobile-card__detail-list">
              <div className="analytics-mobile-card__detail-row">
                <span className="analytics-mobile-card__detail-label">
                  Created By
                </span>
                <span className="analytics-mobile-card__detail-value">
                  {row.createdByName || '-'}
                  {row.createdByPhone ? ` ${row.createdByPhone}` : ''}
                </span>
              </div>
              <div className="analytics-mobile-card__detail-row">
                <span className="analytics-mobile-card__detail-label">
                  Grade
                </span>
                <span className="analytics-mobile-card__detail-value">
                  {row.gradeName}
                  {row.gradeMeta ? ` | ${row.gradeMeta}` : ''}
                </span>
              </div>
              <div className="analytics-mobile-card__detail-row">
                <span className="analytics-mobile-card__detail-label">
                  Location
                </span>
                <span className="analytics-mobile-card__detail-value">
                  {buildLocationLabel(row) || '-'}
                </span>
              </div>
              <div className="analytics-mobile-card__detail-row">
                <span className="analytics-mobile-card__detail-label">
                  Quantity
                </span>
                <span className="analytics-mobile-card__detail-value">
                  {row.totalQty} / {row.minimumQty}
                </span>
              </div>
              <div className="analytics-mobile-card__detail-row">
                <span className="analytics-mobile-card__detail-label">
                  Rate
                </span>
                <span className="analytics-mobile-card__detail-value">
                  {row.rate}
                </span>
              </div>
              {isOffer && (
                <div className="analytics-mobile-card__detail-row">
                  <span className="analytics-mobile-card__detail-label">
                    Created
                  </span>
                  <span className="analytics-mobile-card__detail-value">
                    {row.createdDate} {row.createdTime}
                  </span>
                </div>
              )}
              <div className="analytics-mobile-card__detail-row">
                <span className="analytics-mobile-card__detail-label">
                  Expiry
                </span>
                <span className="analytics-mobile-card__detail-value">
                  {row.expiryDate} {row.expiryTime}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      );
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="report-tab-wrap">
      {/* ── Filter panel ── */}
      <div className="report-filter-panel">
        {/* Radio: By Location / By User */}
        <div className="report-radio-wrap">
          <label className="report-radio-label" htmlFor="report-by-location">
            <input
              id="report-by-location"
              type="radio"
              name="reportType"
              checked={reportType === 'location'}
              onChange={() => {
                setReportType('location');
                setListingType('offer');
              }}
            />
            <span>By Location</span>
          </label>
          <label className="report-radio-label" htmlFor="report-by-user">
            <input
              id="report-by-user"
              type="radio"
              name="reportType"
              checked={reportType === 'user'}
              onChange={() => {
                setReportType('user');
                setListingType('offer');
              }}
            />
            <span>By User</span>
          </label>
        </div>

        {/* Inline filter grid */}
        <div className="report-filters-inline-grid">
          {/* Year / Month */}
          <div className="report-filter-group">
            <label>Year / Month</label>
            <div className="report-month-year-row">
              <select
                className="form-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <Select
                className="react-select"
                classNamePrefix="react-select"
                options={months}
                value={selectedMonths}
                onChange={(options) => {
                  if (!Array.isArray(options) || options.length === 0) {
                    setSelectedMonths([defaultMonthOption]);
                    return;
                  }
                  setSelectedMonths(options);
                }}
                isMulti
                closeMenuOnSelect
                isClearable={false}
                placeholder="Select month(s)"
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                styles={selectMenuStyles}
              />
            </div>
          </div>

          {/* By User: user search */}
          {reportType === 'user' && (
            <div
              className="report-filter-group flex-wide"
              ref={userDropdownRef}
            >
              <label>User Name</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder={
                    loadingTraders
                      ? 'Loading...'
                      : 'Search by name, company or phone'
                  }
                  value={userSearchText}
                  onChange={handleUserSearch}
                  onFocus={() => setShowUserDropdown(true)}
                />
                {showUserDropdown && filteredTraders.length > 0 && (
                  <div className="report-user-dropdown">
                    {filteredTraders.map((trader) => {
                      const companyName = getTraderCompanyName(trader);
                      const contactName = getTraderContactName(trader);
                      const phone = getTraderPhone(trader);
                      const showMeta = contactName !== 'N/A' || phone;
                      return (
                        <div
                          key={trader.id}
                          className="report-user-option"
                          onClick={() => selectUser(trader)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ')
                              selectUser(trader);
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="report-user-item-content">
                            <div className="company-name">{companyName}</div>
                            {showMeta && (
                              <div className="user-meta">
                                {contactName === 'N/A' ? '' : contactName}
                                {phone ? ` | ${phone}` : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* By Location: State + City */}
          {reportType === 'location' && (
            <>
              <div className="report-filter-group flex-wide">
                <label>State</label>
                <Select
                  className="react-select"
                  classNamePrefix="react-select"
                  options={stateOptions}
                  value={selectedState}
                  onChange={handleStateChange}
                  isSearchable
                  isClearable={false}
                  placeholder={
                    statesLoading ? 'Loading states...' : 'All States'
                  }
                  isLoading={statesLoading}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                  styles={selectMenuStyles}
                />
              </div>

              <div className="report-filter-group flex-wide">
                <label>City</label>
                <Select
                  className="react-select"
                  classNamePrefix="react-select"
                  options={cityOptions}
                  value={selectedCity}
                  onChange={(option) =>
                    setSelectedCity(option || ALL_CITIES_OPTION)
                  }
                  isSearchable
                  isClearable={false}
                  isDisabled={selectedState?.value === 'ALL'}
                  isLoading={citiesLoading}
                  placeholder={cityPlaceholder}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                  styles={selectMenuStyles}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Summary cards ── */}
      {reportType === 'location' ? (
        <div className="report-summary-grid">
          <div className="report-summary-card">
            <p>Offer Count / Total offer quantity</p>
            <h5>{locationOfferSummary}</h5>
          </div>
          <div className="report-summary-card">
            <p>Demand count / Total demand quantity</p>
            <h5>{locationDemandSummary}</h5>
          </div>
          <div className="report-summary-card">
            <p>Total Sellers</p>
            <h5>{locationSellerSummary}</h5>
          </div>
          <div className="report-summary-card">
            <p>Total Buyers</p>
            <h5>{locationBuyerSummary}</h5>
          </div>
        </div>
      ) : (
        <div className="report-summary-grid report-summary-grid-user">
          <div className="report-summary-card">
            <p>Offer Count / Total offer quantity</p>
            <h5>{userOfferSummary}</h5>
          </div>
          <div className="report-summary-card">
            <p>Demand count / Total demand quantity</p>
            <h5>{userDemandSummary}</h5>
          </div>
        </div>
      )}

      {/* ── Listing table ── */}
      <div className="report-listing-wrap">
        <div className="report-listing-tabs">
          <div className="report-listing-tabs-row">
            <CustomTabs
              tabs={listingTabs}
              selectedTab={isOffer ? 'Offer' : 'Demand'}
              onTabChange={(tab) => {
                setListingType(tab === 'Offer' ? 'offer' : 'demand');
                setCurrentPage(1);
              }}
              className="mb-0 report-inner-tabs"
            />
            <div className="report-download-actions">
              {csvError && <span className="report-csv-error">{csvError}</span>}
              <Button
                className="gray-btn csv-btn report-csv-btn"
                color="link"
                size="sm"
                type="button"
                onClick={handleDownloadCsv}
                disabled={downloadDisabled}
              >
                {csvDownloading ? (
                  <Spinner
                    color="primary"
                    size="sm"
                    className="small-loader"
                    style={{ marginTop: '1px' }}
                  />
                ) : (
                  <RiDownload2Line size={16} className="fw-bold" />
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="analytics-mobile-list report-mobile-list">
          {renderMobileRows()}
        </div>

        <div className="table-responsive table-border report-desktop-table">
          <table className="table common-table mb-0">
            <thead>
              <tr>
                <th>S.NO.</th>
                <th>
                  {renderFilterHeader(
                    'sellerBuyer',
                    isOffer ? 'SELLER' : 'BUYER',
                    reportType === 'user',
                  )}
                </th>
                <th>{renderFilterHeader('createdBy', 'CREATED BY')}</th>
                <th>{isOffer ? 'OFFER ID' : 'DEMAND ID'}</th>
                <th>{renderFilterHeader('grade', 'GRADE')}</th>
                <th>{renderFilterHeader('location', 'LOCATION')}</th>
                <th>TOTAL QTY</th>
                <th>MIN. QTY</th>
                <th>RATE</th>
                <th>STATUS</th>
                {isOffer && <th>CREATED DATE</th>}
                <th>EXPIRY DATE</th>
              </tr>
            </thead>
            <tbody>{renderTableRows()}</tbody>
          </table>
        </div>

        {/* Pagination */}
        {((reportType === 'location' &&
          !locationReportLoading &&
          !locationReportError) ||
          (reportType === 'user' &&
            hasUserSelected &&
            !userReportLoading &&
            !userReportError)) &&
          (() => {
            let totalCount = 0;
            if (shouldClientPaginate) {
              totalCount = filteredRows.length;
            } else if (reportType === 'location') {
              totalCount = isOffer ? totalOffers : totalDemands;
            } else {
              totalCount = isOffer
                ? userReport?.totalOffers
                : userReport?.totalDemands;
            }
            const totalPages = Math.ceil((Number(totalCount) || 0) / pageSize);
            if (!totalPages) return null;

            return (
              <div className="d-flex mt-3 align-items-center justify-content-end">
                <span className="text-active text-nowrap f-12">
                  {currentPage} of {totalPages}
                </span>
                <select
                  className="page-select form-select table-select f-12 fw-normal mx-3 d-inline-block"
                  value={pageSize}
                  onChange={(e) => {
                    const nextSize = Number(e.target.value);
                    setCurrentPage(1);
                    setPageSize(nextSize);
                  }}
                >
                  {[10, 20, 30, 40, 50].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
                <div className="pagination">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage <= 1}
                  >
                    {'<<'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    {'<'}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage >= totalPages}
                  >
                    {'>'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage >= totalPages}
                  >
                    {'>>'}
                  </button>
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
};

export default ReportTab;
